import React from 'react'
import {
    Box,
    Flex,
    Heading,
    LinkBox,
    LinkOverlay,
    Skeleton,
    Text,
    chakra,
} from '@chakra-ui/react'
import Link from 'next/link'
import { Users } from '@emotion-icons/feather'
import { ResultOf } from '@graphql-typed-document-node/core'
import { gql } from '@urql/core'
import { MapPin } from 'emotion-icons/feather'
import { Company, CompanySizes, getCompanyMainLocationCountry } from 'entities/company'
import { company as mockCompany } from 'entities/company.mock'
import { Location } from 'entities/location'
import { skeletonLocation } from 'entities/location.mock'
import { Tag } from 'entities/tag'
import { routes } from 'routes'
import { CardWrapper } from 'components/CardWrapper'
import { Avatar } from 'components/images/Avatar'
import { Tags } from 'components/tags/Tags'

const UsersIcon = chakra(Users)
const MapPinIcon = chakra(MapPin)

export const FRAGMENT_COMPANY_CARD = gql<
    Pick<
        Company,
        'id' | 'name' | 'slug' | 'logo' | 'description' | 'profileEnabled' | 'size'
    > & {
        tags?: Pick<Tag, 'id' | 'label' | 'slug'>[]
        locations?: Pick<Location, 'city' | 'country'>[]
    }
>`
    fragment CompanyCard on Company {
        id
        name
        slug
        logo
        description
        tags {
            id
            label
            slug
        }
        locations {
            city
            country
        }
        size
        profileEnabled
    }
`

export type CompanyCardData = ResultOf<typeof FRAGMENT_COMPANY_CARD>

const CompanyName = ({
    isLoaded,
    companyProfileURL,
    name,
}: {
    isLoaded: boolean
    companyProfileURL: string
    name: string
}) => {
    return (
        <Skeleton
            isLoaded={isLoaded}
            ml={{ base: '8px', md: 0 }}
            mb={{ base: 0, md: '8px' }}
        >
            <Link href={companyProfileURL} passHref>
                <LinkOverlay>
                    <Heading as="h3" size="md" noOfLines={1}>
                        {name}
                    </Heading>
                </LinkOverlay>
            </Link>
        </Skeleton>
    )
}

const CompanySize = ({ size, isLoaded }: { size: string; isLoaded: boolean }) => {
    return (
        <Flex gridGap="6px" alignItems="center">
            <UsersIcon flexShrink={0} boxSize="19px" strokeWidth={1.5} />

            <Skeleton isLoaded={isLoaded}>
                <Text variant="bodyTextSmall" noOfLines={1}>
                    {size}
                </Text>
            </Skeleton>
        </Flex>
    )
}

const CompanyLocation = ({
    isLoaded,
    location,
    withIcon = false,
}: {
    isLoaded: boolean
    location: string
    withIcon?: boolean
}) => {
    if (!location) {
        return null
    }

    return (
        <>
            <Flex gridGap="6px" alignItems="center">
                {withIcon && <MapPinIcon boxSize="16px" strokeWidth={1.5} />}

                <Skeleton isLoaded={isLoaded}>
                    <Text variant="bodyTextSmall" noOfLines={1}>
                        {location}
                    </Text>
                </Skeleton>
            </Flex>
        </>
    )
}

export const CompanyCard = ({ company }: { company?: CompanyCardData }) => {
    const {
        name = mockCompany.name,
        slug = mockCompany.name,
        logo = mockCompany.logo,
        description = mockCompany.description,
        profileEnabled,
        tags = mockCompany.tags,
        size = 'small',
    } = company ?? {}

    const isLoaded = Boolean(company?.id)
    const companyProfileURL = profileEnabled && slug ? routes.companyProfile(slug) : '#'
    const companyLocation = isLoaded ? getCompanyMainLocationCountry(company) : undefined

    return (
        <LinkBox as={CardWrapper} p={{ base: '20px', md: '36px' }} borderRadius="16px">
            <Flex
                gridGap="24px"
                direction={{ base: 'column', md: 'row' }}
                alignItems="start"
            >
                <Flex alignItems="center" gridGap="12px">
                    {logo && (
                        <Avatar
                            variant="company"
                            src={logo}
                            boxSize={{ base: '40px', md: '80px' }}
                            isLoaded={isLoaded}
                        />
                    )}
                    <Box display={{ base: 'block', md: 'none' }}>
                        <CompanyName
                            isLoaded={isLoaded}
                            companyProfileURL={companyProfileURL}
                            name={name}
                        />
                    </Box>
                </Flex>
                <Flex direction="column">
                    <Box display={{ base: 'none', md: 'block' }}>
                        <CompanyName
                            isLoaded={isLoaded}
                            companyProfileURL={companyProfileURL}
                            name={name}
                        />
                    </Box>
                    <Box
                        w="fit-content"
                        mb="12px"
                        display={
                            isLoaded && !(size || companyLocation) ? 'none' : undefined
                        }
                    >
                        <Flex
                            gridGap={{ base: '8px', md: '26px' }}
                            direction={{ base: 'column-reverse', md: 'row' }}
                        >
                            {size && CompanySizes[size] && (
                                <CompanySize
                                    isLoaded={isLoaded}
                                    size={CompanySizes[size]}
                                />
                            )}
                            <CompanyLocation
                                withIcon
                                isLoaded={isLoaded}
                                location={companyLocation ?? skeletonLocation.country}
                            />
                        </Flex>
                    </Box>
                    <Skeleton
                        isLoaded={isLoaded}
                        mb="16px"
                        w="fit-content"
                        display={isLoaded && !description ? 'none' : undefined}
                    >
                        <Text
                            variant="bodyTextSmall"
                            fontWeight="500"
                            color="text.secondary"
                            noOfLines={3}
                        >
                            {description}
                        </Text>
                    </Skeleton>
                    <Tags
                        tags={tags}
                        buildHref={({ slug }) =>
                            routes.jobsPresetSearch({ tech: { slug } })
                        }
                        isLoaded={isLoaded}
                        // wrap props
                        h="74px"
                        overflow="hidden"
                        mb="13px"
                        // tag props
                        variant="secondary"
                        size="md"
                    />
                </Flex>
            </Flex>
        </LinkBox>
    )
}
